import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import {
  Monitor, Image, Video, FileText, Music, Radio, Type,
  Plus, Trash2, Edit3, Eye, Wifi, WifiOff, Clock,
  Upload, Download, CheckCircle, X, Play, Pause,
  BarChart2, TrendingUp, Activity, Layers, RefreshCw,
  Camera, Send, AlertCircle, Filter, Search, MoreVertical,
  GripVertical, Settings, Copy, Check, Globe, ListVideo
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { format, formatDistanceToNow } from 'date-fns'
import { cs } from 'date-fns/locale'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts'

import { useAuthStore, useAlertStore, useStatsStore, useScreenStore } from '@/stores/stores'
import {
  screensApi, mediaApi, playlistsApi, schedulesApi,
  tickersApi, streamsApi, alertsApi, statsApi, groupsApi
} from '../services/api'
import {
  AppShell, Topbar, Modal, ConfirmDialog, StatusBadge,
  Spinner, PageLoader, SearchBar, EmptyState, ToastContainer
} from '../components/layout/Layout'

// ══════════════════════════════════════════════════════════════
// LOGIN PAGE
// ══════════════════════════════════════════════════════════════
export function LoginPage() {
  const [step, setStep] = useState('login') // login | 2fa
  const [tempToken, setTempToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { login, verify2fa, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const { register, handleSubmit } = useForm()

  useEffect(() => { if (isAuthenticated()) navigate('/dashboard') }, [])

  const onLogin = async (data) => {
    setLoading(true); setError('')
    try {
      const result = await login(data)
      if (result.requires2fa) {
        setTempToken(result.tempToken)
        setStep('2fa')
      } else {
        navigate('/dashboard')
      }
    } catch (e) {
      setError(e.response?.data?.message ?? 'Chyba přihlášení')
    }
    setLoading(false)
  }

  const on2fa = async (data) => {
    setLoading(true); setError('')
    try {
      await verify2fa(tempToken, data.code)
      navigate('/dashboard')
    } catch (e) {
      setError('Nesprávný kód')
    }
    setLoading(false)
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-base)',
      padding: 24,
    }}>
      {/* Background grid effect */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(59,130,246,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(59,130,246,0.03) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
      }} />

      <div style={{
        width: '100%', maxWidth: 400,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        padding: 40,
        position: 'relative',
        boxShadow: 'var(--shadow-lg)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 48, height: 48,
            background: 'var(--accent)',
            borderRadius: 'var(--radius-md)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: 'var(--accent-glow)',
          }}>
            <Layers size={22} color="white" />
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: '1.5rem',
            fontWeight: 800, letterSpacing: '-0.04em',
          }}>
            {step === '2fa' ? 'Dvoufázové ověření' : 'SIGNAGE PLATFORM'}
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 6 }}>
            {step === '2fa' ? 'Zadejte kód z aplikace' : 'Přihlaste se do administrace'}
          </p>
        </div>

        {error && (
          <div style={{
            background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 'var(--radius-md)', padding: '10px 14px',
            color: 'var(--red)', fontSize: '0.8rem', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {step === 'login' ? (
          <form onSubmit={handleSubmit(onLogin)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">E-mail</label>
              <input {...register('email')} type="email" className="form-input" placeholder="admin@firma.cz" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Heslo</label>
              <input {...register('password')} type="password" className="form-input" placeholder="••••••••" />
            </div>
            <button type="submit" className="btn btn-primary w-full" disabled={loading}
              style={{ justifyContent: 'center', marginTop: 8 }}>
              {loading ? <Spinner size={16} /> : 'Přihlásit se'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit(on2fa)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Kód z authenticatoru</label>
              <input {...register('code')} type="text" className="form-input"
                placeholder="000000" maxLength={6} style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.3em', fontSize: '1.2rem', textAlign: 'center' }}
                autoFocus />
            </div>
            <button type="submit" className="btn btn-primary w-full" disabled={loading}
              style={{ justifyContent: 'center' }}>
              {loading ? <Spinner size={16} /> : 'Ověřit'}
            </button>
            <button type="button" className="btn btn-ghost w-full" onClick={() => setStep('login')}
              style={{ justifyContent: 'center', color: 'var(--text-muted)' }}>
              ← Zpět
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// DASHBOARD
// ══════════════════════════════════════════════════════════════
export function DashboardPage() {
  const { data: overview, isLoading } = useQuery({
    queryKey: ['stats-overview'],
    queryFn: () => statsApi.overview().then(r => r.data),
    refetchInterval: 30000,
  })

  const statCards = overview ? [
    { label: 'Obrazovky', value: overview.screens.total, sub: `${overview.screens.online} online`, color: 'var(--accent)', icon: Monitor },
    { label: 'Offline',   value: overview.screens.offline, sub: 'mimo provoz',         color: 'var(--red)',    icon: WifiOff },
    { label: 'Čeká',      value: overview.screens.pending, sub: 'čeká na autorizaci', color: 'var(--yellow)', icon: Monitor },
    { label: 'Média',     value: overview.media.total,  sub: `${overview.media.storage_mb} MB`, color: 'var(--purple)', icon: Image },
    { label: 'Přehrávání dnes', value: overview.playback_today, sub: 'přehrávání', color: 'var(--green)', icon: Play },
  ] : []

  return (
    <AppShell>
      <Topbar title="Dashboard" subtitle="Přehled systému v reálném čase" />
      <main className="app-main">
        {isLoading ? <PageLoader /> : (
          <>
            {/* Stat cards */}
            <div className="grid-4 mb-6">
              {statCards.map(card => (
                <div key={card.label} className="stat-card">
                  <div className="stat-icon" style={{ background: `${card.color}20` }}>
                    <card.icon size={20} color={card.color} />
                  </div>
                  <div>
                    <div className="stat-value">{card.value}</div>
                    <div className="stat-label">{card.label}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{card.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Alerty */}
            {overview?.alerts_unseen > 0 && (
              <div style={{
                background: 'var(--red-dim)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 'var(--radius-lg)',
                padding: '14px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 24,
              }}>
                <AlertCircle size={18} color="var(--red)" />
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--red)' }}>
                    {overview.alerts_unseen} nevyřešených alertů
                  </span>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: '0.8rem' }}>
                    Zkontrolujte sekci Alerty
                  </span>
                </div>
              </div>
            )}

            {/* Screen status grid */}
            <ScreenStatusWidget />
          </>
        )}
      </main>
      <ToastContainer />
    </AppShell>
  )
}

function ScreenStatusWidget() {
  const { data: screens } = useQuery({
    queryKey: ['screens'],
    queryFn: () => screensApi.list().then(r => r.data),
    refetchInterval: 15000,
  })

  const online  = screens?.filter(s => s.status === 'online').length ?? 0
  const offline = screens?.filter(s => s.status === 'offline' || (s.status === 'active' && s.last_seen_at && new Date(s.last_seen_at) < new Date(Date.now() - 5*60*1000))).length ?? 0
  const pending = screens?.filter(s => s.status === 'pending').length ?? 0

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Stav obrazovek</h3>
        <div className="flex items-center gap-3 text-sm text-muted">
          <span style={{ color: 'var(--green)' }}>● {online} online</span>
          <span style={{ color: 'var(--red)' }}>● {offline} offline</span>
          <span style={{ color: 'var(--yellow)' }}>● {pending} čeká</span>
        </div>
      </div>
      <div className="grid-auto">
        {screens?.slice(0, 12).map(screen => (
          <MiniScreenCard key={screen.id} screen={screen} />
        ))}
      </div>
      {!screens?.length && <EmptyState icon={Monitor} title="Žádné obrazovky" text="Přidejte první obrazovku" />}
    </div>
  )
}

function MiniScreenCard({ screen }) {
  const { lastScreenshot } = useScreenStore()
  const shot = lastScreenshot[screen.id]

  return (
    <div className={`screen-card ${screen.status}`}>
      <div className="screen-preview">
        {shot ? (
          <img src={shot.url} alt={screen.name} />
        ) : screen.screenshot_url ? (
          <img src={screen.screenshot_url} alt={screen.name} />
        ) : (
          <div className="screen-preview-placeholder">
            <Monitor size={24} />
            <span style={{ fontSize: '0.68rem' }}>Bez náhledu</span>
          </div>
        )}
      </div>
      <div className="screen-card-body">
        <div className="screen-card-name">{screen.name}</div>
        {screen.tenant?.name && (
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>{screen.tenant.name}</div>
        )}
        <div className="flex items-center gap-2 mt-1">
          <StatusBadge status={screen.status} />
          <span className="screen-card-meta">{screen.resolution ?? '—'}</span>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
// SCREENS PAGE
// ══════════════════════════════════════════════════════════════
export function ScreensPage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [editScreen, setEditScreen] = useState(null)
  const [deleteId, setDeleteId]   = useState(null)
  const [screenshotId, setScreenshotId] = useState(null)
  const { addToast } = useAlertStore()

  const { data: screens, isLoading } = useQuery({
    queryKey: ['screens', filter],
    queryFn:  () => screensApi.list().then(r => r.data),
    refetchInterval: 15000,
  })

  const isOffline = (s) => s.status === 'offline' ||
    (s.status === 'active' && s.last_seen_at && new Date(s.last_seen_at) < new Date(Date.now() - 5*60*1000))
  const isOnline  = (s) => s.status === 'active' && s.last_seen_at &&
    new Date(s.last_seen_at) >= new Date(Date.now() - 5*60*1000)
  const filtered = screens?.filter(s => {
    if (filter === 'online')  return isOnline(s)
    if (filter === 'offline') return isOffline(s)
    if (filter === 'pending') return s.status === 'pending'
    return true
  })?.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.ip_address ?? '').includes(search)
  )

  const authorize = async (id) => {
    await screensApi.authorize(id)
    qc.invalidateQueries(['screens'])
    addToast({ type: 'success', title: 'Obrazovka autorizována' })
  }

  const requestShot = async (id) => {
    await screensApi.requestScreenshot(id)
    setScreenshotId(id)
    addToast({ type: 'info', title: 'Screenshot vyžádán' })
  }

  const deleteScreen = async (id) => {
    await screensApi.delete(id)
    qc.invalidateQueries(['screens'])
    addToast({ type: 'success', title: 'Obrazovka smazána' })
  }

  return (
    <AppShell>
      <Topbar
        title="Obrazovky"
        subtitle={`${screens?.length ?? 0} obrazovek`}
      />
      <main className="app-main">
        {/* Filters */}
        <div className="flex items-center gap-3 mb-6" style={{ flexWrap: 'wrap' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Hledat obrazovky..." />
          <div className="flex items-center gap-2">
            {['all', 'online', 'offline', 'pending'].map(f => (
              <button
                key={f}
                className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'Vše' : f === 'online' ? 'Online' : f === 'offline' ? 'Offline' : 'Čekající'}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? <PageLoader /> : (
          <>
            {/* Pending screens – priority upozornění */}
            {filtered?.filter(s => s.status === 'pending').map(screen => (
              <div key={screen.id} style={{
                background: 'var(--yellow-dim)', border: '1px solid rgba(245,158,11,0.3)',
                borderRadius: 'var(--radius-lg)', padding: '14px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 12,
              }}>
                <div className="flex items-center gap-3">
                  <Monitor size={18} color="var(--yellow)" />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{screen.name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      {screen.ip_address} · {screen.os}
                      {screen.tenant?.name && <span style={{ marginLeft: 8, color: 'var(--accent-light)' }}>· {screen.tenant.name}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn btn-success btn-sm" onClick={() => authorize(screen.id)}>
                    <CheckCircle size={14} /> Autorizovat
                  </button>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteId(screen.id)}
                    style={{ color: 'var(--red)' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}

            {/* Screens grid */}
            <div className="grid-auto">
              {filtered?.filter(s => s.status !== 'pending').map(screen => (
                <ScreenCard
                  key={screen.id}
                  screen={screen}
                  onEdit={() => setEditScreen(screen)}
                  onDelete={() => setDeleteId(screen.id)}
                  onScreenshot={() => requestShot(screen.id)}
                />
              ))}
            </div>

            {!filtered?.length && (
              <EmptyState icon={Monitor} title="Žádné obrazovky" text="Spusťte klientskou aplikaci pro registraci první obrazovky" />
            )}
          </>
        )}
      </main>

      {/* Edit Modal */}
      <ScreenEditModal
        screen={editScreen}
        onClose={() => setEditScreen(null)}
        onSaved={() => { qc.invalidateQueries(['screens']); setEditScreen(null) }}
      />

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteScreen(deleteId)}
        title="Smazat obrazovku"
        message="Opravdu chcete smazat tuto obrazovku? Tato akce je nevratná."
        danger
      />
      <ToastContainer />
    </AppShell>
  )
}

function ScreenCard({ screen, onEdit, onDelete, onScreenshot }) {
  const { lastScreenshot } = useScreenStore()
  const shot = lastScreenshot[screen.id]

  return (
    <div className={`screen-card ${screen.status}`}>
      <div className="screen-preview">
        {(shot?.url || screen.screenshot_url) ? (
          <img src={shot?.url ?? screen.screenshot_url} alt={screen.name} />
        ) : (
          <div className="screen-preview-placeholder">
            {screen.status === 'online'
              ? <><Wifi size={28} color="var(--green)" /><span style={{ fontSize: '0.7rem' }}>Online</span></>
              : <><Monitor size={28} /><span style={{ fontSize: '0.7rem' }}>Bez náhledu</span></>
            }
          </div>
        )}
        {screen.status === 'online' && (
          <div style={{ position: 'absolute', top: 8, left: 8 }}>
            <span className="online-indicator" />
          </div>
        )}
      </div>

      <div className="screen-card-body">
        <div className="screen-card-name">{screen.name}</div>
        <div className="flex items-center gap-2 mt-1">
          <StatusBadge status={screen.status} />
        </div>
        <div className="screen-card-meta mt-1">
          {screen.ip_address && <span>{screen.ip_address}</span>}
          {screen.resolution && <span> · {screen.resolution}</span>}
          {screen.last_seen_at && (
            <span> · {formatDistanceToNow(new Date(screen.last_seen_at), { locale: cs, addSuffix: true })}</span>
          )}
        </div>
        {screen.groups?.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {screen.groups.map(g => (
              <span key={g} style={{
                background: 'var(--accent-dim)', color: 'var(--accent-light)',
                borderRadius: 4, padding: '1px 6px', fontSize: '0.65rem',
                fontFamily: 'var(--font-mono)',
              }}>{g}</span>
            ))}
          </div>
        )}
      </div>

      <div className="screen-card-actions">
        <button className="btn btn-ghost btn-sm btn-icon" onClick={onScreenshot} title="Screenshot">
          <Camera size={14} />
        </button>
        <button className="btn btn-ghost btn-sm btn-icon" onClick={onEdit} title="Nastavení">
          <Settings size={14} />
        </button>
        <button className="btn btn-ghost btn-sm btn-icon" onClick={onDelete}
          title="Smazat" style={{ color: 'var(--red)', marginLeft: 'auto' }}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  )
}

function ScreenEditModal({ screen, onClose, onSaved }) {
  const { register, handleSubmit, reset } = useForm()
  const { addToast } = useAlertStore()

  useEffect(() => { if (screen) reset(screen) }, [screen])

  const onSubmit = async (data) => {
    await screensApi.update(screen.id, data)
    addToast({ type: 'success', title: 'Obrazovka uložena' })
    onSaved()
  }

  return (
    <Modal
      open={!!screen}
      onClose={onClose}
      title={`Nastavení: ${screen?.name}`}
      size="lg"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Zrušit</button>
          <button className="btn btn-primary" onClick={handleSubmit(onSubmit)}>Uložit</button>
        </>
      }
    >
      <div className="form-row form-row-2">
        <div className="form-group">
          <label className="form-label">Název</label>
          <input {...register('name')} className="form-input" />
        </div>
        <div className="form-group">
          <label className="form-label">Lokace</label>
          <input {...register('location_name')} className="form-input" placeholder="Prodejna Praha 1" />
        </div>
      </div>
      <div className="form-row form-row-2">
        <div className="form-group">
          <label className="form-label">Cache interval (min)</label>
          <input {...register('cache_interval_minutes')} type="number" className="form-input" />
        </div>
        <div className="form-group">
          <label className="form-label">Limit cache (GB)</label>
          <input {...register('cache_limit_gb')} type="number" className="form-input" />
        </div>
      </div>
      <div className="form-row form-row-2">
        <div className="form-group">
          <label className="form-label">Výchozí doba zobrazení (s)</label>
          <input {...register('default_slide_duration')} type="number" className="form-input" />
        </div>
        <div className="form-group">
          <label className="form-label">Orientace</label>
          <select {...register('orientation')} className="form-select">
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Poznámky</label>
        <textarea {...register('notes')} className="form-textarea" />
      </div>
      {/* Hardware info */}
      {screen && (
        <div style={{
          background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
          padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
        }}>
          {[
            ['OS', `${screen.os ?? '—'} ${screen.os_version ?? ''}`],
            ['CPU', screen.cpu ?? '—'],
            ['RAM', screen.ram_mb ? `${Math.round(screen.ram_mb / 1024)} GB` : (screen.ram_gb ? `${screen.ram_gb} GB` : '—')],
            ['Disk volný', screen.disk_free_mb ? `${Math.round(screen.disk_free_mb / 1024)} GB` : '—'],
            ['Disk celkem', screen.disk_total_mb ? `${Math.round(screen.disk_total_mb / 1024)} GB` : '—'],
            ['Prohlížeč', screen.browser ? `${screen.browser} ${screen.browser_version ?? ''}` : '—'],
            ['Rozlišení', screen.resolution ?? '—'],
            ['Orientace', screen.orientation ?? '—'],
            ['Síť', screen.network_type ?? '—'],
            ['Jazyk', screen.language ?? '—'],
            ['Timezone', screen.timezone ?? '—'],
            ['Player', screen.player_type ?? '—'],
            ['App verze', screen.app_version ?? '—'],
          ].map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>{k}</div>
              <div style={{ fontSize: '0.8rem' }}>{v}</div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════
// MEDIA PAGE
// ══════════════════════════════════════════════════════════════
export function MediaPage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [uploadProgress, setUploadProgress] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const { addToast } = useAlertStore()

  const { data: media, isLoading } = useQuery({
    queryKey: ['media', typeFilter],
    queryFn: () => mediaApi.list(typeFilter !== 'all' ? { type: typeFilter } : {}).then(r => r.data),
  })

  const filtered = media?.data?.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase())
  ) ?? media?.filter(m => m.name.toLowerCase().includes(search.toLowerCase())) ?? []

  const onDrop = useCallback(async (files) => {
    for (const file of files) {
      const fd = new FormData()
      fd.append('file', file)
      try {
        setUploadProgress({ name: file.name, progress: 0 })
        await mediaApi.upload(fd, p => setUploadProgress({ name: file.name, progress: p }))
        qc.invalidateQueries(['media'])
        addToast({ type: 'success', title: `${file.name} nahráno` })
      } catch (e) {
        addToast({ type: 'error', title: `Chyba nahrávání: ${file.name}` })
      }
    }
    setUploadProgress(null)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
      'video/*': [],
      'audio/*': [],
      'application/pdf': [],
      'application/vnd.ms-powerpoint': [],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': [],
    },
  })

  const deleteMedia = async (id) => {
    await mediaApi.delete(id)
    qc.invalidateQueries(['media'])
    addToast({ type: 'success', title: 'Médium smazáno' })
  }

  const typeIcons = { image: Image, video: Video, audio: Music, pdf: FileText, presentation: FileText, stream: Radio }

  return (
    <AppShell>
      <Topbar title="Médiabook" subtitle={`${filtered.length} souborů`} />
      <main className="app-main">
        {/* Dropzone */}
        <div {...getRootProps()} className={`dropzone mb-6 ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          <Upload size={32} className="dropzone-icon" />
          <div className="dropzone-text">
            {isDragActive ? 'Pusťte soubory sem...' : 'Přetáhněte soubory sem nebo klikněte pro výběr'}
          </div>
          <div className="dropzone-hint">PNG · JPG · GIF · WEBP · MP4 · MKV · PDF · PPTX · MP3 · WAV · max 2GB</div>
        </div>

        {/* Upload progress */}
        {uploadProgress && (
          <div style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 16,
          }}>
            <div className="flex items-center justify-between mb-2">
              <span style={{ fontSize: '0.8rem' }}>{uploadProgress.name}</span>
              <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--accent-light)' }}>
                {uploadProgress.progress}%
              </span>
            </div>
            <div style={{ background: 'var(--bg-base)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
              <div style={{ background: 'var(--accent)', height: '100%', width: `${uploadProgress.progress}%`, transition: 'width 0.1s' }} />
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4" style={{ flexWrap: 'wrap' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Hledat média..." />
          <div className="flex items-center gap-2">
            {['all', 'image', 'video', 'pdf', 'presentation', 'audio'].map(t => (
              <button
                key={t}
                className={`btn btn-sm ${typeFilter === t ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setTypeFilter(t)}
              >
                {t === 'all' ? 'Vše' : t}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? <PageLoader /> : filtered.length > 0 ? (
          <div className="media-grid">
            {filtered.map(m => {
              const Icon = typeIcons[m.type] ?? Image
              return (
                <div key={m.id} className="media-card" style={{ position: 'relative' }}>
                  <button
                    onClick={() => setDeleteId(m.id)}
                    style={{
                      position: 'absolute', top: 6, right: 6, zIndex: 10,
                      background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6,
                      color: 'white', cursor: 'pointer', padding: '4px 6px',
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                  <div className="media-thumb">
                    {(m.thumbnail_url || m.url)
                      ? <img src={m.thumbnail_url ?? m.url} alt={m.name} />
                      : <Icon size={28} className="media-thumb-icon" />
                    }
                  </div>
                  <span className="media-type-badge">{m.type}</span>
                  {m.conversion_status === 'processing' && (
                    <div style={{
                      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Spinner size={24} />
                    </div>
                  )}
                  <div className="media-card-info">
                    <div className="media-card-name" title={m.name}>{m.name}</div>
                    <div className="media-card-meta">{m.filesize_mb} MB · {m.type}</div>
                  </div>
                  <button
                    className="btn btn-ghost btn-icon"
                    onClick={() => setDeleteId(m.id)}
                    style={{
                      position: 'absolute', top: 6, left: 6,
                      background: 'rgba(0,0,0,0.6)', color: 'white',
                      opacity: 0, transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )
            })}
          </div>
        ) : (
          <EmptyState icon={Image} title="Žádná média" text="Nahrajte první soubor přetažením do oblasti výše" />
        )}
      </main>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteMedia(deleteId)}
        title="Smazat médium"
        message="Tato akce je nevratná. Médium bude odstraněno ze všech playlistů."
        danger
      />
      <ToastContainer />
    </AppShell>
  )
}

// ══════════════════════════════════════════════════════════════
// PLAYLISTS PAGE
// ══════════════════════════════════════════════════════════════
export function PlaylistsPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const { addToast } = useAlertStore()

  const { data: playlists, isLoading } = useQuery({
    queryKey: ['playlists'],
    queryFn: () => playlistsApi.list().then(r => r.data),
  })

  const { register, handleSubmit, reset } = useForm()

  const createPlaylist = async (data) => {
    await playlistsApi.create(data)
    qc.invalidateQueries(['playlists'])
    addToast({ type: 'success', title: 'Playlist vytvořen' })
    setCreateOpen(false)
    reset()
  }

  const deletePlaylist = async (id) => {
    await playlistsApi.delete(id)
    qc.invalidateQueries(['playlists'])
    addToast({ type: 'success', title: 'Playlist smazán' })
  }

  const formatDuration = (s) => {
    if (!s) return '—'
    const m = Math.floor(s / 60), sec = s % 60
    return `${m}:${String(sec).padStart(2, '0')}`
  }

  return (
    <AppShell>
      <Topbar
        title="Playlisty"
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
            <Plus size={14} /> Nový playlist
          </button>
        }
      />
      <main className="app-main">
        {isLoading ? <PageLoader /> : playlists?.length > 0 ? (
          <div className="card">
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Název</th>
                    <th>Položky</th>
                    <th>Délka</th>
                    <th>Verze</th>
                    <th>Upraveno</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {playlists.map(p => (
                    <tr key={p.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{p.name}</div>
                        {p.description && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{p.description}</div>}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)' }}>{p.items_count}</td>
                      <td style={{ fontFamily: 'var(--font-mono)' }}>{formatDuration(p.total_duration)}</td>
                      <td><span className="badge badge-info">v{p.version}</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                        {p.updated_at ? formatDistanceToNow(new Date(p.updated_at), { locale: cs, addSuffix: true }) : '—'}
                      </td>
                      <td>
                        <div className="flex items-center gap-2" style={{ justifyContent: 'flex-end' }}>
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setEditId(p.id)}>
                            <Edit3 size={14} />
                          </button>
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteId(p.id)}
                            style={{ color: 'var(--red)' }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={ListVideo}
            title="Žádné playlisty"
            text="Vytvořte první playlist a přiřaďte ho obrazovkám"
            action={<button className="btn btn-primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> Vytvořit playlist</button>}
          />
        )}
      </main>

      {/* Create modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nový playlist"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Zrušit</button>
            <button className="btn btn-primary" onClick={handleSubmit(createPlaylist)}>Vytvořit</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Název playlistu</label>
          <input {...register('name')} className="form-input" placeholder="Hlavní prodejna" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Popis (volitelný)</label>
          <textarea {...register('description')} className="form-textarea" placeholder="Popis playlistu..." />
        </div>
      </Modal>

      {/* Edit modal */}
      {editId && (
        <PlaylistEditorModal
          playlistId={editId}
          onClose={() => setEditId(null)}
          onSaved={() => { qc.invalidateQueries(['playlists']); setEditId(null) }}
        />
      )}

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deletePlaylist(deleteId)}
        title="Smazat playlist"
        message="Playlist bude odebrán ze všech plánování."
        danger
      />
      <ToastContainer />
    </AppShell>
  )
}

function PlaylistEditorModal({ playlistId, onClose, onSaved }) {
  const { data: playlist } = useQuery({
    queryKey: ['playlist', playlistId],
    queryFn: () => playlistsApi.get(playlistId).then(r => r.data),
  })
  const { data: allMedia } = useQuery({
    queryKey: ['media'],
    queryFn: () => mediaApi.list().then(r => r.data?.data ?? r.data),
  })
  const { data: allStreams = [] } = useQuery({
    queryKey: ['streams'],
    queryFn: () => streamsApi.list().then(r => r.data),
  })
  const { data: allTickers = [] } = useQuery({
    queryKey: ['tickers'],
    queryFn: () => tickersApi.list().then(r => r.data),
  })

  const [mediaTab, setMediaTab] = useState('media')
  const [items, setItems] = useState([])
  const [tickerItems, setTickerItems] = useState([])
  const [previewItem, setPreviewItem] = useState(null)
  const { addToast } = useAlertStore()

  useEffect(() => {
    if (playlist) {
      setItems((playlist.items ?? []).filter(i => i.type !== 'ticker'))
      setTickerItems((playlist.items ?? []).filter(i => i.type === 'ticker'))
    }
  }, [playlist])

  const addMedia = (media) => setItems(prev => [...prev, {
    id: Date.now(), order: prev.length, type: 'media', media_id: media.id,
    media: { id: media.id, name: media.name, type: media.type,
             thumbnail_url: media.thumbnail_url, url: media.url, duration: media.duration },
    duration: media.type === 'video' ? (media.duration ?? 30) : 10,
    transition: 'fade',
  }])

  const addStream = (stream) => setItems(prev => [...prev, {
    id: Date.now(), order: prev.length, type: 'stream', stream_id: stream.id,
    media: { name: stream.name, type: 'stream' }, duration: 3600, transition: 'none',
  }])

  const toggleTicker = (ticker) => setTickerItems(prev => {
    const exists = prev.find(t => t.ticker_id === ticker.id)
    if (exists) return prev.filter(t => t.ticker_id !== ticker.id)
    return [...prev, {
      id: Date.now(), type: 'ticker', ticker_id: ticker.id,
      ticker: { id: ticker.id, name: ticker.name, bg_color: ticker.bg_color, text_color: ticker.text_color },
      duration: 0, transition: 'none',
    }]
  })

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx))
  const updateDuration = (idx, val) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, duration: parseInt(val) || 10 } : it))
  const moveItem = (idx, dir) => setItems(prev => {
    const next = [...prev], t = idx + dir
    if (t < 0 || t >= next.length) return prev
    ;[next[idx], next[t]] = [next[t], next[idx]]
    return next
  })

  const save = async () => {
    console.log('[Playlist] items před uložením:', JSON.stringify(items.map(it => ({ type: it.type, media_id: it.media_id, stream_id: it.stream_id, ticker_id: it.ticker_id }))))
    const allItems = [
      ...items.map((it, i) => ({
        order: i, type: it.type,
        media_id: it.media_id ?? null, stream_id: it.stream_id ?? null,
        duration: it.duration, transition: it.transition ?? 'none', transition_duration: 500,
      })),
      ...tickerItems.map((it, i) => ({
        order: items.length + i, type: 'ticker', ticker_id: it.ticker_id,
        duration: 0, transition: 'none', transition_duration: 0,
      })),
    ]
    await playlistsApi.update(playlistId, { items: allItems })
    addToast({ type: 'success', title: 'Playlist uložen' })
    onSaved()
  }

  const typeIcons = { image: Image, video: Video, audio: Music, pdf: FileText, presentation: FileText, stream: Radio }

  const MediaThumb = ({ item }) => {
    const m = item.media
    if (!m) return <div style={{ width: 48, height: 32, flexShrink: 0 }} />
    if (m.type === 'video') return (
      <div onClick={() => setPreviewItem(item)} style={{ width: 48, height: 32, flexShrink: 0, borderRadius: 4, overflow: 'hidden', background: '#000', cursor: 'pointer', position: 'relative' }}>
        {m.thumbnail_url ? <img src={m.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Video size={14} color="#fff" /></div>}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Play size={7} color="#000" fill="#000" /></div>
        </div>
      </div>
    )
    if (m.type === 'pdf' || m.type === 'presentation') return (
      <div onClick={() => setPreviewItem(item)} style={{ width: 48, height: 32, flexShrink: 0, borderRadius: 4, overflow: 'hidden', background: 'var(--bg-base)', cursor: 'pointer', position: 'relative', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {m.thumbnail_url ? <img src={m.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <FileText size={12} color="var(--text-muted)" />}
        {m.pages?.length > 0 && <div style={{ position: 'absolute', bottom: 1, right: 1, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '0.5rem', padding: '1px 3px', borderRadius: 2, fontFamily: 'var(--font-mono)' }}>{m.pages.length}s</div>}
      </div>
    )
    if (m.thumbnail_url || m.url) return (
      <div style={{ width: 48, height: 32, flexShrink: 0, borderRadius: 4, overflow: 'hidden' }}>
        <img src={m.thumbnail_url ?? m.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    )
    const Icon = typeIcons[m.type] ?? Image
    return (
      <div style={{ width: 48, height: 32, flexShrink: 0, borderRadius: 4, background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
        <Icon size={14} color="var(--text-muted)" />
      </div>
    )
  }

  const LibraryThumb = ({ m }) => {
    const Icon = typeIcons[m.type] ?? Image
    if (m.type === 'video') return (
      <div style={{ aspectRatio: '16/9', position: 'relative', background: '#000', borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0', overflow: 'hidden' }}>
        {m.thumbnail_url ? <img src={m.thumbnail_url} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Video size={24} color="#555" /></div>}
        <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: '0.6rem', padding: '1px 4px', borderRadius: 3, fontFamily: 'var(--font-mono)' }}>
          {m.duration ? `${Math.floor(m.duration/60)}:${String(m.duration%60).padStart(2,'0')}` : 'VIDEO'}
        </div>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Play size={10} color="#fff" fill="#fff" /></div>
        </div>
      </div>
    )
    if (m.type === 'pdf' || m.type === 'presentation') return (
      <div style={{ aspectRatio: '16/9', position: 'relative', background: 'var(--bg-base)', borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0', overflow: 'hidden', border: '1px solid var(--border)' }}>
        {m.thumbnail_url ? <img src={m.thumbnail_url} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}><FileText size={28} color="var(--accent)" /><span style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{m.type}</span></div>}
        {m.pages_count > 0 && <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '0.6rem', padding: '1px 5px', borderRadius: 3, fontFamily: 'var(--font-mono)' }}>{m.pages_count} str.</div>}
      </div>
    )
    return (
      <div className="media-thumb" style={{ aspectRatio: '16/9' }}>
        {(m.thumbnail_url || m.url) ? <img src={m.thumbnail_url ?? m.url} alt={m.name} /> : <Icon size={20} className="media-thumb-icon" />}
      </div>
    )
  }

  return (
    <>
      <Modal open={true} onClose={onClose} title={`Editace: ${playlist?.name ?? '...'}`} size="xl"
        footer={<><button className="btn btn-secondary" onClick={onClose}>Zrušit</button><button className="btn btn-primary" onClick={save}>Uložit playlist</button></>}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          <div>
            <div style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10 }}>Položky ({items.length})</div>
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              {items.length === 0 && <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>Přidejte média ze seznamu vpravo</div>}
              {items.map((item, idx) => (
                <div key={item.id ?? idx} className="playlist-item" style={{ gap: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button className="btn btn-ghost btn-icon" style={{ padding: 2 }} onClick={() => moveItem(idx, -1)} disabled={idx === 0}><span style={{ fontSize: '0.6rem' }}>▲</span></button>
                    <button className="btn btn-ghost btn-icon" style={{ padding: 2 }} onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}><span style={{ fontSize: '0.6rem' }}>▼</span></button>
                  </div>
                  <MediaThumb item={item} />
                  <div className="playlist-item-info" style={{ flex: 1 }}>
                    <div className="playlist-item-name">{item.media?.name ?? 'Stream'}</div>
                    <div className="playlist-item-meta" style={{ display: 'flex', gap: 4 }}>
                      <span>{item.media?.type}</span>
                      {item.media?.type === 'video' && item.media?.duration && <span style={{ color: 'var(--accent-light)' }}>· {Math.floor(item.media.duration/60)}:{String(item.media.duration%60).padStart(2,'0')}</span>}
                      {(item.media?.type === 'pdf' || item.media?.type === 'presentation') && item.media?.pages?.length > 0 && <span style={{ color: 'var(--accent-light)' }}>· {item.media.pages.length} str.</span>}
                    </div>
                  </div>
                  <input type="number" className="playlist-duration-input" value={item.duration} onChange={e => updateDuration(idx, e.target.value)} min={1} />
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>s</span>
                  <button className="btn btn-ghost btn-icon" onClick={() => removeItem(idx)} style={{ color: 'var(--red)', padding: 4 }}><X size={13} /></button>
                </div>
              ))}
            </div>
            {tickerItems.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: '0.7rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Tickery ({tickerItems.length})</div>
                {tickerItems.map(ti => (
                  <div key={ti.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 4, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                    <div style={{ width: 28, height: 16, borderRadius: 3, flexShrink: 0, background: ti.ticker?.bg_color ?? '#1F4E79', border: '1px solid var(--border)' }} />
                    <span style={{ flex: 1, fontSize: '0.8rem' }}>{ti.ticker?.name}</span>
                    <span style={{ fontSize: '0.65rem', color: 'var(--accent-light)', fontFamily: 'var(--font-mono)' }}>ticker</span>
                    <button className="btn btn-ghost btn-icon" onClick={() => setTickerItems(prev => prev.filter(t => t.ticker_id !== ti.ticker_id))} style={{ color: 'var(--red)', padding: 4 }}><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10 }}>Mediální knihovna</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {[{ id: 'media', label: 'Média' }, { id: 'streams', label: `Streamy (${allStreams.length})` }, { id: 'tickers', label: `Tickery (${allTickers.length})` }].map(tab => (
                <button key={tab.id} className={`btn btn-sm ${mediaTab === tab.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setMediaTab(tab.id)}>{tab.label}</button>
              ))}
            </div>
            {mediaTab === 'streams' && (
              <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allStreams.map(s => (
                  <div key={s.id} onClick={() => addStream(s)} style={{ padding: '10px 12px', cursor: 'pointer', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Radio size={16} color="var(--accent)" />
                    <div><div style={{ fontWeight: 500, fontSize: '0.82rem' }}>{s.name}</div><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{s.type?.toUpperCase()} · {s.status}</div></div>
                  </div>
                ))}
                {allStreams.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center', padding: 20 }}>Žádné streamy</div>}
              </div>
            )}
            {mediaTab === 'tickers' && (
              <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 4 }}>Klikněte pro přidání/odebrání</div>
                {allTickers.map(t => {
                  const isSelected = tickerItems.some(ti => ti.ticker_id === t.id)
                  return (
                    <div key={t.id} onClick={() => toggleTicker(t)} style={{ padding: '10px 12px', cursor: 'pointer', borderRadius: 'var(--radius-md)', border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`, background: isSelected ? 'var(--accent-dim)' : 'var(--bg-elevated)', display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s' }}>
                      <div style={{ width: 40, height: 22, borderRadius: 4, flexShrink: 0, background: t.bg_color ?? '#1F4E79', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '0.55rem', color: t.text_color ?? '#fff', fontFamily: 'var(--font-mono)' }}>▶ ticker</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: '0.82rem' }}>{t.name}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{t.messages_count} zpráv · rychlost {t.speed}</div>
                      </div>
                      {isSelected && <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Check size={10} color="#fff" /></div>}
                    </div>
                  )
                })}
                {allTickers.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center', padding: 20 }}>Žádné tickery</div>}
              </div>
            )}
            {mediaTab === 'media' && (
              <div style={{ maxHeight: 420, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {(allMedia ?? []).map(m => (
                  <div key={m.id} className="media-card" style={{ cursor: 'pointer' }} onClick={() => addMedia(m)}>
                    <LibraryThumb m={m} />
                    <div className="media-card-info">
                      <div className="media-card-name" style={{ fontSize: '0.7rem' }}>{m.name}</div>
                      <div className="media-card-meta" style={{ fontSize: '0.62rem' }}>
                        {m.filesize_mb} MB
                        {m.type === 'video' && m.duration && ` · ${Math.floor(m.duration/60)}:${String(m.duration%60).padStart(2,'0')}`}
                        {(m.type === 'pdf' || m.type === 'presentation') && m.pages_count && ` · ${m.pages_count} str.`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>
      {previewItem && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setPreviewItem(null)}>
          <div style={{ position: 'relative', maxWidth: '80vw' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setPreviewItem(null)} style={{ position: 'absolute', top: -36, right: 0, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
            {previewItem.media?.type === 'video' && <video controls autoPlay style={{ maxWidth: '80vw', maxHeight: '75vh', borderRadius: 8 }}><source src={previewItem.media.url} /></video>}
            {previewItem.media?.type === 'pdf' && <iframe src={`${previewItem.media.url}#toolbar=0`} style={{ width: '70vw', height: '75vh', border: 'none', borderRadius: 8 }} title={previewItem.media.name} />}
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', textAlign: 'center', marginTop: 8 }}>{previewItem.media?.name}</div>
          </div>
        </div>
      )}
    </>
  )
}


// ══════════════════════════════════════════════════════════════
// ALERTS PAGE
// ══════════════════════════════════════════════════════════════
export function AlertsPage() {
  const { alerts, unseenCount, loadAlerts, markSeen, markAllSeen } = useAlertStore()
  const [onlyUnseen, setOnlyUnseen] = useState(false)

  useEffect(() => { loadAlerts(onlyUnseen ? { unseen: true } : {}) }, [onlyUnseen])

  const severityColors = { critical: 'var(--red)', error: 'var(--red)', warning: 'var(--yellow)', info: 'var(--accent)' }
  const typeLabels = {
    screen_offline: 'Obrazovka offline',
    screen_online: 'Obrazovka online',
    screen_error: 'Chyba obrazovky',
    media_conversion_failed: 'Konverze média selhala',
    storage_low: 'Málo místa',
    virus_found: 'Virus nalezen',
    queue_failed: 'Chyba queue',
    custom: 'Vlastní',
  }

  return (
    <AppShell>
      <Topbar
        title="Alerty"
        subtitle={unseenCount > 0 ? `${unseenCount} nevyřešených` : 'Vše vyřešeno'}
        actions={
          unseenCount > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={markAllSeen}>
              <CheckCircle size={14} /> Označit vše jako přečtené
            </button>
          )
        }
      />
      <main className="app-main">
        <div className="flex items-center gap-3 mb-4">
          <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
            <label className="toggle-switch">
              <input type="checkbox" checked={onlyUnseen} onChange={e => setOnlyUnseen(e.target.checked)} />
              <span className="toggle-slider" />
            </label>
            <span style={{ fontSize: '0.8rem' }}>Jen nevyřešené</span>
          </label>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {alerts.length === 0 ? (
            <EmptyState icon={CheckCircle} title="Žádné alerty" text="Systém funguje správně" />
          ) : (
            alerts.map(alert => (
              <div
                key={alert.id}
                className={`alert-item ${!alert.seen_at ? 'unseen' : ''}`}
                onClick={() => !alert.seen_at && markSeen(alert.id)}
                style={{ cursor: !alert.seen_at ? 'pointer' : 'default' }}
              >
                <div
                  className="alert-dot"
                  style={{ background: severityColors[alert.severity] ?? 'var(--text-muted)' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{alert.title}</span>
                    <span style={{
                      background: 'var(--bg-base)', padding: '1px 6px',
                      borderRadius: 4, fontSize: '0.65rem', fontFamily: 'var(--font-mono)',
                      color: 'var(--text-muted)',
                    }}>{typeLabels[alert.type] ?? alert.type}</span>
                  </div>
                  {alert.message && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{alert.message}</div>}
                </div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                  {alert.created_at ? formatDistanceToNow(new Date(alert.created_at), { locale: cs, addSuffix: true }) : ''}
                </div>
                {!alert.seen_at && (
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: 'var(--accent)', flexShrink: 0,
                  }} />
                )}
              </div>
            ))
          )}
        </div>
      </main>
      <ToastContainer />
    </AppShell>
  )
}

// ══════════════════════════════════════════════════════════════
// STATISTICS PAGE
// ══════════════════════════════════════════════════════════════
export function StatisticsPage() {
  const [dateFrom, setDateFrom] = useState(format(new Date(Date.now() - 7 * 86400000), 'yyyy-MM-dd'))
  const [dateTo,   setDateTo]   = useState(format(new Date(), 'yyyy-MM-dd'))

  const { data: overview } = useQuery({
    queryKey: ['stats-overview'],
    queryFn: () => statsApi.overview().then(r => r.data),
  })

  const { data: playback } = useQuery({
    queryKey: ['stats-playback', dateFrom, dateTo],
    queryFn: () => statsApi.playback({ from: dateFrom, to: dateTo }).then(r => r.data),
  })

  // Agregace dat pro graf
  const chartData = React.useMemo(() => {
    if (!playback?.data) return []
    const byDay = {}
    playback.data.forEach(log => {
      const day = log.played_at?.slice(0, 10)
      if (day) byDay[day] = (byDay[day] ?? 0) + 1
    })
    return Object.entries(byDay).map(([date, count]) => ({ date, count })).sort((a, b) => a.date.localeCompare(b.date))
  }, [playback])

  return (
    <AppShell>
      <Topbar title="Statistiky" subtitle="Přehled přehrávání a výkonu" />
      <main className="app-main">
        {/* Summary cards */}
        {overview && (
          <div className="grid-4 mb-6">
            {[
              { label: 'Přehrávání dnes',       value: overview.playback_today, color: 'var(--accent)' },
              { label: 'Aktivní obrazovky',     value: overview.screens.online, color: 'var(--green)' },
              { label: 'Celkem médií',           value: overview.media.total,   color: 'var(--purple)' },
              { label: 'Playlistů',              value: overview.playlists,     color: 'var(--yellow)' },
            ].map(card => (
              <div key={card.label} className="stat-card">
                <div>
                  <div className="stat-value" style={{ color: card.color }}>{card.value}</div>
                  <div className="stat-label">{card.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Playback chart */}
        <div className="card mb-6">
          <div className="card-header">
            <h3 className="card-title">Přehrávání v čase</h3>
            <div className="flex items-center gap-3">
              <input type="date" className="form-input" style={{ width: 'auto' }}
                value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              <span style={{ color: 'var(--text-muted)' }}>—</span>
              <input type="date" className="form-input" style={{ width: 'auto' }}
                value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--accent)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: 'var(--text-primary)' }}
              />
              <Area type="monotone" dataKey="count" stroke="var(--accent)" strokeWidth={2} fill="url(#grad)" name="Přehrávání" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Recent playback */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Záznamy přehrávání</h3>
          </div>
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Čas</th>
                  <th>Obrazovka</th>
                  <th>Médium</th>
                  <th>Playlist</th>
                  <th>Délka</th>
                </tr>
              </thead>
              <tbody>
                {playback?.data?.slice(0, 50).map((log, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>
                      {log.played_at ? format(new Date(log.played_at), 'dd.MM HH:mm') : '—'}
                    </td>
                    <td>{log.screen?.name ?? log.screen_id}</td>
                    <td style={{ maxWidth: 200 }}><div className="truncate">{log.media?.name ?? log.item_name ?? '—'}</div></td>
                    <td style={{ color: 'var(--text-muted)' }}>{log.playlist?.name ?? '—'}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{log.duration_played}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      <ToastContainer />
    </AppShell>
  )
}
